import React, { useEffect, useState } from 'react';
import { Field } from 'formik';

import {
  InputUI,
  DatePickerUI,
  AutoCompleteSelectUI,
  AutoCompleteSelectServiceUI,
  FormErrorUI,
  FormLabelUI,
  AddButtonUi,
  CheckboxUI,
} from '../../Interface';
import { multiply } from 'lodash';
import useParam from '../../../context/ParamContext';
import { generateAutocompleteItems } from '../../../utils/helper';
import masterDataService from '../../../service/masterData.service';
import { itemAutocompleteRequestBuilder, itemAutocompleteService } from '../../../utils/itemAutocomplete';
import useConfig from '../../../context/ConfigContext';

const ProductionFields = ({
  viewField,
  selectedValues,
  values,
  validateField,
  setFieldValue,
  errors,
  autocompleteOptions,
  autocompleteData,
  getSourceData,
  calculateLineQty,
  setShowConfirmDialogForStatus,
  setProjectDialogOpen,
}) => {
  const { config } = useConfig();
  const { getParam, params } = useParam();
  const [resolvedFinishedItemName, setResolvedFinishedItemName] = useState('');

  const selectedWarehouse = autocompleteData?.warehouse?.find((warehouse) => warehouse.code === values.whsCode);
  const isBinManaged = Boolean(selectedWarehouse?.binManaged);
  const binOptions = generateAutocompleteItems(selectedWarehouse?.bin, 'binCode', 'binId');
  const selectedBin = selectedWarehouse?.bin?.find((bin) => bin.binId === values.binId);

  const normalizeSingleItemResponse = (response) => {
    if (Array.isArray(response?.body)) {
      return response.body[0] ?? null;
    }

    if (Array.isArray(response)) {
      return response[0] ?? null;
    }

    return response?.body ?? response ?? null;
  };

  const getItemMetaByCode = async (itemCode) => {
    if (!itemCode) {
      return null;
    }

    try {
      const response = await masterDataService.itemsFromTableToFormByItemCode(itemCode);
      return normalizeSingleItemResponse(response);
    } catch {
      return null;
    }
  };

  useEffect(() => {
    getParam();
  }, []);

  useEffect(() => {
    getSourceData(values.articleNumber);
  }, [values.articleNumber]);

  useEffect(() => {
    calculateLineQty();
  }, [values.productionQuantity]);

  useEffect(() => {
    const selectedFinishedCode = values.finishedItemCode ?? null;
    const selectedFinishedName =
      values.finishedItemCode && values.finishedItemCode === selectedValues?.finishedItemCode
        ? selectedValues?.finishedItemName
        : null;

    if (!selectedFinishedCode) {
      setResolvedFinishedItemName('');
      return;
    }

    if (selectedFinishedName) {
      setResolvedFinishedItemName(selectedFinishedName);
      return;
    }

    let cancelled = false;

    masterDataService
      .itemsFromTableToFormByItemCode(selectedFinishedCode)
      .then((response) => {
        if (cancelled) {
          return;
        }

        const selectedItem = normalizeSingleItemResponse(response);
        setResolvedFinishedItemName(selectedItem?.itemName ?? '');
      })
      .catch(() => {
        if (!cancelled) {
          setResolvedFinishedItemName('');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [values.finishedItemCode, selectedValues?.finishedItemCode, selectedValues?.finishedItemName]);

  useEffect(() => {
    const selectedItemCode = values.articleNumber ?? null;
    const selectedItemName =
      values.articleName ||
      (values.articleNumber && values.articleNumber === selectedValues?.articleNumber
        ? selectedValues?.articleName
        : '');

    if (!selectedItemCode || selectedItemName) {
      return;
    }

    let cancelled = false;

    getItemMetaByCode(selectedItemCode)
      .then((response) => {
        if (cancelled) {
          return;
        }

        const selectedItem = normalizeSingleItemResponse(response);
        if (selectedItem?.itemName && !values.articleName) {
          setFieldValue('articleName', selectedItem.itemName);
        }
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [values.articleNumber, values.articleName, selectedValues?.articleName, setFieldValue]);

  return (
    <>
      <div className="grid grid-cols-4 gap-x-4 gap-y-2 mx-4">
        <div className="col-span-3 md:col-span-1">
          <Field
            type="text"
            name="workingNumber"
            disabled
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Munkaszám" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.workingNumber} />}
            InputLabelProps={{ shrink: values.workingNumber !== '' }}
            value={values?.workingNumber ?? ''}
          />
        </div>
        <div className="col-span-3 md:col-span-1">
          <div className="flex items-center">
            <Field
              type="autocomplete"
              name="projectId"
              component={AutoCompleteSelectUI}
              fullWidth
              label={<FormLabelUI text="Projekt" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.projectId} />}
              onChange={(_e, newVal) => {
                setFieldValue('projectId', newVal?.value ?? null).then(() => {
                  validateField('projectId');
                });
              }}
              disabled={viewField}
              InputLabelProps={{ shrink: values.projectId !== '' }}
              selectedValue={values.projectId}
              selectedLabelValue={selectedValues?.project?.projectName}
              dataset={autocompleteOptions.project}
            />
            <AddButtonUi onClick={() => setProjectDialogOpen(true)} disabled={viewField} />
          </div>
        </div>
        <div className="col-span-2 ">
          <Field
            type="autocomplete"
            name="articleNumber"
            component={AutoCompleteSelectServiceUI}
            fullWidth
            label={<FormLabelUI text="Cikk" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.articleNumber} />}
            onChange={async (_e, newVal) => {
              const selectedItemCode = newVal?.value ?? null;
              setFieldValue('articleNumber', selectedItemCode);

              if (!selectedItemCode) {
                setFieldValue('me', null);
                setFieldValue('articleName', null);
                setFieldValue('productionOperation', []);
                setFieldValue('productionItem', []);
                setFieldValue('weightPerItem', 0);
                setFieldValue('weight', 0);
                await validateField('articleNumber');
                return;
              }

              const selectedItem = await getItemMetaByCode(selectedItemCode);

              setFieldValue('me', selectedItem?.quantityUnitId ?? null);
              setFieldValue('articleName', selectedItem?.itemName ?? null);
              setFieldValue('productionOperation', []);
              setFieldValue('productionItem', []);
              await validateField('articleNumber');
              setFieldValue('weightPerItem', selectedItem?.weight ?? 0);
              setFieldValue('weight', multiply(selectedItem?.weight ?? 0, values.baseQuantity ?? 1));
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.articleNumber !== '' }}
            selectedValue={values.articleNumber ?? null}
            selectedLabelValue={
              [values.articleNumber, values.articleName].filter(Boolean).join(' - ') || values.articleNumber || ''
            }
            service={itemAutocompleteService}
            serviceRequestBuilder={itemAutocompleteRequestBuilder}
          />
        </div>
        <div className="col-span-3 md:col-span-1">
          <Field
            type="number"
            name="productionQuantity"
            //as={InputUI}
            component={InputUI}
            fullWidth
            label={<FormLabelUI text="Gyártandó mennyiség" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.productionQuantity} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.productionQuantity !== '' }}
            onChange={async (e) => {
              const value = parseFloat(e.target?.value);
              await setFieldValue('productionQuantity', e.target?.value ? value : null);
              await validateField('productionQuantity');

              const findedItem = await getItemMetaByCode(values.articleNumber);
              setFieldValue('weight', multiply(findedItem?.weight ?? 0, value));
              setFieldValue('weightPerItem', findedItem?.weight ?? 0);
            }}
            value={values.productionQuantity}
          />
        </div>

        <div className="col-span-3 md:col-span-1">
          <Field
            type="autocomplete"
            name="me"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="ME" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.me} />}
            onChange={(_e, newVal) => {
              setFieldValue('me', newVal?.value ?? null).then(() => {
                validateField('me');
              });
            }}
            disabled
            InputLabelProps={{ shrink: values.me !== '' }}
            selectedValue={values.me}
            selectedLabelValue={selectedValues?.me}
            dataset={autocompleteOptions.quantityUnit}
          />
        </div>

        <div className="col-span-3 md:col-span-1 ">
          <Field
            type="date"
            name="startDate"
            component={DatePickerUI}
            value={values.startDate}
            fullWidth
            label={<FormLabelUI text="GY. tervezett kezdete" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.startDate} />}
            onChange={(date) => {
              setFieldValue('startDate', date).then(() => {
                validateField('startDate');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.startDate !== '' }}
          />
        </div>
        <div className="col-span-3 md:col-span-1">
          <Field
            type="date"
            name="endDate"
            component={DatePickerUI}
            value={values.endDate}
            fullWidth
            label={<FormLabelUI text="GY. tervezett vége" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.endDate} />}
            onChange={(date) => {
              setFieldValue('endDate', date).then(() => {
                validateField('endDate');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.endDate !== '' }}
          />
        </div>
        <div className="col-span-3 md:col-span-1">
          <Field
            type="autocomplete"
            name="productionStatusId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Gyártás státusza" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.productionStatusId} />}
            onChange={(_e, newVal) => {
              setFieldValue('productionStatusId', newVal?.value ?? null).then(() => {
                validateField('productionStatusId');
              });
              if (newVal?.value && values.productionOperation.length > 0) {
                setShowConfirmDialogForStatus(true);
              }
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.productionStatusId !== '' }}
            selectedValue={values.productionStatusId}
            selectedLabelValue={selectedValues?.productionStatus?.name}
            dataset={autocompleteOptions.prodStatus}
          />
        </div>
        <div className="col-span-3 md:col-span-1">
          <Field
            type="autocomplete"
            name="priorityId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Gyártás priorítása" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.priorityId} />}
            onChange={(_e, newVal) => {
              setFieldValue('priorityId', newVal?.value ?? null).then(() => {
                validateField('priorityId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.priorityId !== '' }}
            selectedValue={values.priorityId}
            selectedLabelValue={selectedValues?.priority?.itemName}
            dataset={autocompleteOptions.priority}
          />
        </div>
        <div className="col-span-3 md:col-span-1 ">
          <Field
            type="text"
            name="baseDocNum"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Vevői rendelés szám" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.baseDocNum} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.baseDocNum !== '' && values.baseDocNum !== null }}
          />
        </div>
        <div className="col-span-3 md:col-span-1">
          <Field
            type="autocomplete"
            name="companyId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Vevő neve" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.companyId} />}
            onChange={(_e, newVal) => {
              setFieldValue('companyId', newVal?.value ?? null).then(() => {
                validateField('companyId');
              });
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.companyId !== '' }}
            selectedValue={values.companyId}
            selectedLabelValue={selectedValues?.company?.companyName}
            dataset={autocompleteOptions.company}
          />
        </div>
        <div className="col-span-3 md:col-span-1 ">
          <Field
            type="text"
            name="baseId"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Vevői rendelés azonosító" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.baseId} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.baseId !== '' && values.baseId !== null }}
          />
        </div>
        <div className="col-span-3 md:col-span-1 ">
          <Field
            type="text"
            name="baseLine"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Vevői rendelés sora" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.baseLine} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.baseLine !== '' && values.baseLine !== null }}
          />
        </div>
        <div className="col-span-3 md:col-span-1 ">
          <Field
            type="text"
            name="reference"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Vevőhivatkozási szám" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.reference} />}
            disabled={viewField}
            InputLabelProps={{ shrink: values.reference !== '' && values.reference !== null }}
          />
        </div>
        <div className="col-span-2 ">
          <Field
            type="autocomplete"
            name="finishedItemCode"
            component={AutoCompleteSelectServiceUI}
            fullWidth
            label={<FormLabelUI text="Késztermék" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.finishedItemCode} />}
            onChange={async (_e, newVal) => {
              setFieldValue('finishedItemCode', newVal?.value ?? null);

              await validateField('finishedItemCode');
            }}
            disabled={viewField}
            InputLabelProps={{ shrink: values.finishedItemCode !== '' }}
            selectedValue={values.finishedItemCode ?? null}
            selectedLabelValue={
              [values.finishedItemCode, resolvedFinishedItemName].filter(Boolean).join(' - ') ||
              values.finishedItemCode ||
              ''
            }
            service={itemAutocompleteService}
            serviceRequestBuilder={itemAutocompleteRequestBuilder}
          />
        </div>
        <div className="col-span-3 md:col-span-1">
          <Field
            type="autocomplete"
            name="userId"
            component={AutoCompleteSelectUI}
            fullWidth
            label={<FormLabelUI text="Tulajdonos" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.userId} />}
            onChange={(_e, newVal) => {
              setFieldValue('userId', newVal?.value ?? null).then(() => {
                validateField('userId');
              });
            }}
            disabled
            InputLabelProps={{ shrink: values.userId !== '' }}
            selectedValue={values.userId}
            selectedLabelValue={selectedValues?.user?.userName}
            dataset={autocompleteOptions.user}
          />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-x-4 gap-y-2 mx-4">
        <div className="col-span-4 md:col-span-1">
          <Field
            type="autocomplete"
            name="whsCode"
            component={AutoCompleteSelectUI}
            fullWidth
            disabled={viewField}
            label={<FormLabelUI text="Raktár" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.whsCode} />}
            InputLabelProps={{ shrink: Boolean(values.whsCode) }}
            onChange={(_e, newVal) => {
              setFieldValue('whsCode', newVal?.value ?? null).then(() => {
                setFieldValue('binId', null);
                validateField('whsCode');
              });
            }}
            selectedValue={values.whsCode}
            selectedLabelValue={autocompleteData?.warehouse?.find((p) => p.code === values.whsCode)?.code}
            dataset={autocompleteOptions?.warehouse}
          />
        </div>
        {isBinManaged && (
          <div className="col-span-4 md:col-span-1">
            <Field
              type="autocomplete"
              name="binId"
              component={AutoCompleteSelectUI}
              fullWidth
              disabled={viewField}
              label={<FormLabelUI text="Rakhely" />}
              variant="standard"
              helperText={<FormErrorUI message={errors.binId} />}
              InputLabelProps={{ shrink: Boolean(values.binId) }}
              onChange={(_e, newVal) => {
                setFieldValue('binId', newVal?.value ?? null).then(() => {
                  validateField('binId');
                });
              }}
              selectedValue={values.binId}
              selectedLabelValue={selectedBin?.binCode}
              dataset={binOptions}
            />
          </div>
        )}
      </div>
      <div className="grid grid-cols-4 gap-x-4 gap-y-2 mx-4">
        <div className="col-span-4 md:col-span-1">
          <Field
            type="number"
            name="weight"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Súly" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.weight} />}
            disabled
            InputLabelProps={{ shrink: values.weight !== '' && values.weight !== null }}
          />
        </div>
        <div className="col-span-4 md:col-span-1">
          <Field
            type="number"
            name="weightPerItem"
            as={InputUI}
            fullWidth
            label={<FormLabelUI text="Súly / Tétel" />}
            variant="standard"
            helperText={<FormErrorUI message={errors.weightPerItem} />}
            disabled
            InputLabelProps={{ shrink: values.weightPerItem !== '' && values.weightPerItem !== null }}
          />
        </div>
        {params?.ENABLE_VARIANT === 'Y' && (
          <>
            <div className="col-span-4 md:col-span-2">
              <Field
                type="autocomplete"
                name="variantItemCode"
                component={AutoCompleteSelectUI}
                fullWidth
                label={<FormLabelUI text="Variáns cikk" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.itemCode} />}
                onChange={(_e, newVal) => {
                  setFieldValue('variantItemCode', newVal?.value ?? null).then(() => {
                    validateField('variantItemCode');
                  });
                }}
                disabled={true}
                InputLabelProps={{ shrink: values.variantItemCode !== '' }}
                selectedValue={values.variantItemCode}
                selectedLabelValue={selectedValues?.variantItemCode?.name}
                dataset={autocompleteOptions?.items}
              />
            </div>
            <div className="col-span-4 md:col-span-2">
              <Field
                type="autocomplete"
                name="variantFinishedItemCode"
                component={AutoCompleteSelectUI}
                fullWidth
                label={<FormLabelUI text="Késztermék variáns" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.itemCode} />}
                onChange={(_e, newVal) => {
                  setFieldValue('variantFinishedItemCode', newVal?.value ?? null).then(() => {
                    validateField('variantFinishedItemCode');
                  });
                }}
                disabled={true}
                InputLabelProps={{ shrink: values.variantFinishedItemCode !== '' }}
                selectedValue={values.variantFinishedItemCode}
                selectedLabelValue={selectedValues?.variantFinishedItemCode?.name}
                dataset={autocompleteOptions?.items}
              />
            </div>
            <div className="col-span-4 md:col-span-2">
              <Field
                type="text"
                name="variantNote"
                as={InputUI}
                fullWidth
                label={<FormLabelUI text="Variáns leírás" />}
                variant="standard"
                helperText={<FormErrorUI message={errors.variantNote} />}
                disabled={true}
                InputLabelProps={{ shrink: values.variantNote && values.variantNote !== '' }}
              />
            </div>
          </>
        )}
        {config?.issueToProdReg && (
          <div className="md:col-span-2 col-span-4 pt-2">
            <Field
              name="skipIssueToProdReg"
              type="checkbox"
              as={CheckboxUI}
              fullWidth
              label={<FormLabelUI text="Anyagkiadás mellőzése" />}
              disabled={viewField}
            />
          </div>
        )}
        {params.PRODUCTION_NON_PRODUCTIVE_WORK_FIELD === 'Y' && (
          <div className="md:col-span-2 col-span-4 pt-2">
            <Field
              name="nonProductiveWork"
              type="checkbox"
              as={CheckboxUI}
              fullWidth
              label={<FormLabelUI text="Nem produktív munka" />}
              disabled={viewField}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default ProductionFields;
